import { useCallback, useEffect, useState } from "react";
import { useJourney } from "@/context/JourneyContext";

/**
 * useCityShortlist — LocalStorage-backed shortlist of city slugs for
 * the Place pillar. Shared across the /cities index, /cities/matcher,
 * and every /cities/:slug detail page so a student can build one
 * consistent list wherever they are in the flow.
 *
 * Mirrors the shape/behaviour of useCompareShortlist so the two feel
 * consistent (same event pattern, same cap-based add, same cross-tab
 * sync via storage + custom event).
 *
 * Usage:
 *   const { slugs, toggle, has, add, remove, clear, full, cap,
 *           compareHref } = useCityShortlist();
 */
const KEY = "wua:cityShortlist";
const CAP = 4;
const EVT = "wua:cityShortlist:change";

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x) => typeof x === "string").slice(0, CAP);
  } catch { return []; }
}

function write(next) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next.slice(0, CAP)));
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {}
}

const SEEDED_KEY = "wua:cityShortlist:seededAt";

export function useCityShortlist() {
  const [slugs, setSlugs] = useState(read);
  const { profile } = useJourney();
  const confirmed = profile?.where?.cities_confirmed;
  const confirmedAt = profile?.where?.cities_confirmed_at;

  // When the account's saved cities are newer than what this browser last took from them
  // (e.g. changed on another device), start the working list from the saved cities.
  useEffect(() => {
    const list = (confirmed || []).map((c) => (typeof c === "string" ? c : c?.slug)).filter(Boolean);
    if (!list.length || !confirmedAt) return;
    let seeded = null;
    try { seeded = localStorage.getItem(SEEDED_KEY); } catch { /* ignore */ }
    if (seeded === confirmedAt) return;
    try { localStorage.setItem(SEEDED_KEY, confirmedAt); } catch { /* ignore */ }
    write(list);
    setSlugs(list.slice(0, CAP));
  }, [confirmed, confirmedAt]);

  useEffect(() => {
    const sync = () => setSlugs(read());
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const has = useCallback((slug) => slugs.includes(slug), [slugs]);

  const toggle = useCallback((slug) => {
    const cur = read();
    let next;
    if (cur.includes(slug)) next = cur.filter((x) => x !== slug);
    else if (cur.length >= CAP) next = cur; // full — no-op
    else next = [...cur, slug];
    write(next);
    setSlugs(next);
    return next;
  }, []);

  const add = useCallback((slug) => {
    const cur = read();
    if (cur.includes(slug) || cur.length >= CAP) return cur;
    const next = [...cur, slug];
    write(next);
    setSlugs(next);
    return next;
  }, []);

  const remove = useCallback((slug) => {
    const next = read().filter((x) => x !== slug);
    write(next);
    setSlugs(next);
  }, []);

  const clear = useCallback(() => {
    write([]);
    setSlugs([]);
  }, []);

  const compareHref = slugs.length >= 2
    ? `/cities/compare?slugs=${slugs.join(",")}`
    : null;

  return {
    slugs, has, toggle, add, remove, clear, compareHref,
    cap: CAP, full: slugs.length >= CAP,
  };
}
